import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PortfolioManagementPage from './PortfolioManagementPage';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Upload, FileText, CheckCircle, AlertCircle, FileSpreadsheet,
  RefreshCw, Eye, X, Check, Info, Building2, Users, Home,
  CandlestickChart, PieChart, Landmark, Shield, Briefcase,
  ChevronRight, Database, ArrowUpRight, TrendingUp, TrendingDown,
  Link2, Folder, Trash2, Loader2, ExternalLink, FolderOpen, CloudOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur } from '@/lib/market';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SOURCE_ICON_BY_KEY = {
  contract_notes: FileText, mf_cas: FileSpreadsheet, nps: FileText,
  demat_holding: FileSpreadsheet, pms_aif: FileText, broker_ledger: FileSpreadsheet
};
const SOURCE_COLOR_BY_KEY = {
  contract_notes: 'bg-[#1a1a1a]',
  mf_cas: 'bg-[#2a2a2a]',
  nps: 'bg-[#1a1a1a]',
  demat_holding: 'bg-[#1a1a1a]',
  pms_aif: 'bg-[#1a1a1a]',
  broker_ledger: 'bg-[#a8a8a8]'
};

// Import categories — India
const IMPORT_CATEGORIES_IN = [
  {
    id: 'public_markets',
    label: 'Public Markets',
    icon: CandlestickChart,
    color: 'bg-[#1a1a1a]',
    sources: ['CDSL / NSDL Demat Statement', 'Broker Contract Notes', 'Zerodha, Groww, Angel One…'],
    formats: 'PDF, CSV, XLSX',
  },
  {
    id: 'mutual_funds',
    label: 'Mutual Funds',
    icon: PieChart,
    color: 'bg-[#1a1a1a]',
    sources: ['CAMS CAS Statement', 'NSDL CAS Statement', 'MF Central'],
    formats: 'PDF, XML, XLSX',
  },
  {
    id: 'nps',
    label: 'NPS / EPF',
    icon: Shield,
    color: 'bg-[#1a1a1a]',
    sources: ['NSDL eNPS Statement', 'EPFO Passbook', 'NPS Trust Portal'],
    formats: 'PDF, XLSX',
  },
  {
    id: 'bonds',
    label: 'Bonds / FDs',
    icon: Landmark,
    color: 'bg-[#1a1a1a]',
    sources: ['RBI Retail Direct', 'Bank FD Statements', 'NSDL Bond Statement'],
    formats: 'PDF, CSV',
  },
  {
    id: 'pms_aif',
    label: 'PMS / AIF',
    icon: Briefcase,
    color: 'bg-[#1a1a1a]',
    sources: ['PMS Manager Reports', 'AIF Quarterly Statements', 'Custom Excel Template'],
    formats: 'PDF, XLSX',
  },
  {
    id: 'real_estate',
    label: 'Real Estate',
    icon: Home,
    color: 'bg-[#1a1a1a]',
    sources: ['Sale Deed / Registry', 'Property Tax Documents', 'Manual Entry'],
    formats: 'PDF, Manual',
  },
  {
    id: 'startups',
    label: 'Startup Investments',
    icon: Users,
    color: 'bg-[#1a1a1a]',
    sources: ['Term Sheets', 'Share Certificates', 'Cap Table Export'],
    formats: 'PDF, XLSX, CSV',
  },
  {
    id: 'international',
    label: 'International',
    icon: Building2,
    color: 'bg-[#1a1a1a]',
    sources: ['Schwab / Interactive Brokers', 'Vanguard / Fidelity Statements', 'LRS Remittance Records'],
    formats: 'PDF, CSV, XLSX',
  },
];

// Import categories — US
const IMPORT_CATEGORIES_US = [
  {
    id: 'brokerage',
    label: 'Brokerage Accounts',
    icon: CandlestickChart,
    color: 'bg-[#1a1a1a]',
    sources: ['Charles Schwab Statement', 'Fidelity Portfolio Report', 'Interactive Brokers Activity'],
    formats: 'PDF, CSV, XLSX',
  },
  {
    id: 'etf_funds',
    label: 'ETFs & Mutual Funds',
    icon: PieChart,
    color: 'bg-[#1a1a1a]',
    sources: ['Vanguard Statement', 'BlackRock / iShares Report', 'Fidelity Fund Statement'],
    formats: 'PDF, CSV, XLSX',
  },
  {
    id: 'tax_docs',
    label: 'Tax Documents',
    icon: FileText,
    color: 'bg-[#1a1a1a]',
    sources: ['1099-B (Capital Gains)', '1099-DIV (Dividends)', '1099-INT (Interest)'],
    formats: 'PDF, CSV',
  },
  {
    id: 'retirement',
    label: '401(k) / IRA',
    icon: Shield,
    color: 'bg-[#1a1a1a]',
    sources: ['Fidelity 401(k) Statement', 'Vanguard IRA Statement', 'Schwab Retirement Report'],
    formats: 'PDF, XLSX',
  },
  {
    id: 'real_estate',
    label: 'Real Estate',
    icon: Home,
    color: 'bg-[#1a1a1a]',
    sources: ['Property Deed / Title', 'Mortgage Statement', 'Manual Entry'],
    formats: 'PDF, Manual',
  },
  {
    id: 'startups',
    label: 'Startup Investments',
    icon: Users,
    color: 'bg-[#1a1a1a]',
    sources: ['Term Sheets', 'Share Certificates / 409A', 'Cap Table (Carta / Pulley)'],
    formats: 'PDF, XLSX, CSV',
  },
  {
    id: 'crypto',
    label: 'Crypto / Digital Assets',
    icon: Database,
    color: 'bg-[#1a1a1a]',
    sources: ['Coinbase Statement', 'Kraken Export', 'Ledger / Hardware Wallet'],
    formats: 'CSV, XLSX',
  },
];

// Supported document formats — India
const SUPPORTED_FORMATS_IN = [
  { label: 'CDSL CAS', desc: 'Consolidated Account Statement from CDSL' },
  { label: 'NSDL CAS', desc: 'Consolidated Account Statement from NSDL' },
  { label: 'Broker Contract Notes', desc: 'Zerodha, Groww, Angel One, ICICI Direct, HDFC Sec…' },
  { label: 'CAMS CAS', desc: 'MF Consolidated Account Statement via CAMS' },
  { label: 'MF Central', desc: 'Unified MF statement portal' },
  { label: 'NSDL eNPS', desc: 'NPS statement from NSDL CRA' },
  { label: 'EPFO Passbook', desc: 'Provident Fund passbook PDF' },
  { label: 'RBI Retail Direct', desc: 'G-Sec / T-Bill statements' },
  { label: 'PMS Reports', desc: 'Portfolio Management Service monthly/quarterly reports' },
];

// Supported document formats — US
const SUPPORTED_FORMATS_US = [
  { label: 'Schwab CSV', desc: 'Charles Schwab transaction & position export' },
  { label: 'Fidelity PDF', desc: 'Fidelity brokerage statement (monthly/quarterly)' },
  { label: 'IBKR Activity', desc: 'Interactive Brokers activity statement (CSV/XLSX)' },
  { label: '1099-B', desc: 'IRS form for capital gains from broker' },
  { label: '1099-DIV', desc: 'Dividend and distribution income form' },
  { label: 'Vanguard PDF', desc: 'Vanguard account statement for ETFs/MFs' },
  { label: 'Carta Cap Table', desc: 'Startup cap table export from Carta / Pulley' },
  { label: 'Coinbase CSV', desc: 'Crypto transaction history from Coinbase' },
];

export default function DataImportPage() {
  const { user } = useAuth();
  const { importSources: contextImportSources, brokers: contextBrokers } = useAppData();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [importing, setImporting] = useState(false);

  // Eye-button preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewHoldings, setPreviewHoldings] = useState([]);
  const [previewTxns, setPreviewTxns] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── SharePoint state ─────────────────────────────────────────────────────
  const [spConnections, setSpConnections] = useState([]);
  const [spDocs, setSpDocs] = useState([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spSyncing, setSpSyncing] = useState(null);  // connection_id being synced
  const [spProgress, setSpProgress] = useState({});  // { [connection_id]: { completed, failed, total, eta } }
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderConnId, setFolderConnId] = useState(null);
  const [folderUrl, setFolderUrl] = useState('');

  const isUS = user?.market === 'US';

  // ── SharePoint helpers ───────────────────────────────────────────────────
  const fetchSpConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/data-room/sharepoint/connections`);
      setSpConnections(res.data.connections || []);
    } catch { /* silent */ }
  }, []);

  const fetchSpDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/data-room/documents`);
      setSpDocs(res.data.documents || []);
    } catch { /* silent */ }
  }, []);

  const connectSharePoint = async () => {
    setSpLoading(true);
    try {
      const res = await axios.get(`${API}/data-room/sharepoint/connect`);
      const { auth_url } = res.data;
      const popup = window.open(auth_url, 'sharepoint-auth', 'width=600,height=700,left=200,top=100');
      const handler = (e) => {
        if (e.origin !== window.location.origin) return;
        if (e.data?.type === 'SHAREPOINT_AUTH_SUCCESS') {
          toast.success('SharePoint connected!');
          fetchSpConnections();
          window.removeEventListener('message', handler);
          if (popup) popup.close();
        } else if (e.data?.type === 'SHAREPOINT_AUTH_ERROR') {
          toast.error(`Connection failed: ${e.data.error}`);
          window.removeEventListener('message', handler);
        }
      };
      window.addEventListener('message', handler);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate SharePoint connection');
    } finally {
      setSpLoading(false);
    }
  };

  const openFolderDialog = (connId) => {
    setFolderConnId(connId);
    const existing = spConnections.find(c => c.id === connId);
    setFolderUrl(existing?.folder_url || '');
    setFolderDialogOpen(true);
  };

  const saveFolder = async () => {
    if (!folderUrl.trim()) { toast.error('Enter a SharePoint folder URL'); return; }
    try {
      const res = await axios.post(`${API}/data-room/sharepoint/connections/${folderConnId}/set-folder`, { folder_url: folderUrl.trim() });
      toast.success(`Folder set: ${res.data.folder_name}`);
      fetchSpConnections();
      setFolderDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set folder');
    }
  };

  const syncConnection = async (connId) => {
    setSpSyncing(connId);
    setSpProgress(prev => ({ ...prev, [connId]: { completed: 0, failed: 0, total: 0, eta: 0, done: false } }));
    try {
      const res = await fetch(`${API}/data-room/sharepoint/connections/${connId}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.event === 'progress') {
              setSpProgress(prev => ({ ...prev, [connId]: { completed: ev.completed, failed: ev.failed, total: ev.total, eta: ev.eta_seconds, done: false } }));
            } else if (ev.event === 'done') {
              setSpProgress(prev => ({ ...prev, [connId]: { ...prev[connId], done: true, message: ev.message } }));
              toast.success(ev.message || `Sync complete — ${ev.successful} files processed`);
              fetchSpConnections();
              fetchSpDocs();
            } else if (ev.event === 'error') {
              toast.error(ev.message);
            }
          } catch { /* malformed SSE line */ }
        }
      }
    } catch (err) {
      toast.error('Sync failed. Please try again.');
    } finally {
      setSpSyncing(null);
    }
  };

  const deleteConnection = async (connId) => {
    if (!window.confirm('Delete this SharePoint connection?')) return;
    try {
      await axios.delete(`${API}/data-room/sharepoint/connections/${connId}`);
      toast.success('Connection deleted');
      fetchSpConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/data-room/documents/${docId}`);
      setSpDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch { toast.error('Could not delete document'); }
  };

  useEffect(() => { fetchSpConnections(); fetchSpDocs(); }, [fetchSpConnections, fetchSpDocs]);

  const importSources = contextImportSources.length
    ? contextImportSources.map(s => ({
        id: s.key, name: s.label,
        description: s.data?.description || '',
        formats: (s.data?.formats || []).join(', '),
        brokers: s.data?.brokers_count > 0 ? `${s.data.brokers_count}+` : '',
        icon: SOURCE_ICON_BY_KEY[s.key] || FileText,
        color: SOURCE_COLOR_BY_KEY[s.key] || 'bg-[#1a1a1a]'
      }))
    : [];

  const popularBrokers = isUS
    ? ['Charles Schwab', 'Fidelity', 'Interactive Brokers', 'E*TRADE', 'TD Ameritrade', 'Robinhood', 'Vanguard', 'Merrill Lynch']
    : contextBrokers.map(b => b.label);

  const IMPORT_CATEGORIES = isUS ? IMPORT_CATEGORIES_US : IMPORT_CATEGORIES_IN;
  const SUPPORTED_FORMATS  = isUS ? SUPPORTED_FORMATS_US : SUPPORTED_FORMATS_IN;

  // Recent Imports — market-aware representative entries
  const recentImports = isUS ? [
    { id: 1, date: '08 Apr 2026', source: 'Schwab Transaction History', type: 'Stocks & ETFs', records: 10, status: 'success', assetFilter: 'stocks' },
    { id: 2, date: '05 Apr 2026', source: 'Fidelity Monthly Statement', type: 'ETFs', records: 4, status: 'success', assetFilter: 'etf' },
    { id: 3, date: '01 Apr 2026', source: '1099-B (Capital Gains)', type: 'Tax Document', records: 6, status: 'success', assetFilter: 'stocks' },
    { id: 4, date: '28 Mar 2026', source: 'Interactive Brokers CSV', type: 'Stocks', records: 3, status: 'success', assetFilter: 'stocks' },
    { id: 5, date: '20 Mar 2026', source: 'Vanguard ETF Statement', type: 'ETFs', records: 2, status: 'partial', assetFilter: 'etf' },
  ] : [
    { id: 1, date: '09 Apr 2026', source: 'Zerodha Contract Notes', type: 'Stocks', records: 7, status: 'success', assetFilter: 'stocks' },
    { id: 2, date: '08 Apr 2026', source: 'CAMS CAS Statement',     type: 'Mutual Funds', records: 2, status: 'success', assetFilter: 'mf' },
    { id: 3, date: '06 Apr 2026', source: 'Groww Contract Notes',   type: 'Stocks', records: 3, status: 'success', assetFilter: 'stocks' },
    { id: 4, date: '01 Apr 2026', source: 'Zerodha ETF Statement',  type: 'Gold / ETF', records: 1, status: 'success', assetFilter: 'gold' },
    { id: 5, date: '25 Mar 2026', source: 'Groww MF Portfolio',     type: 'Mutual Funds', records: 2, status: 'partial', assetFilter: 'mf' },
  ];

  const openPreview = async (item) => {
    setPreviewTitle(`${item.source} — ${item.date}`);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const [holdingsRes, txnsRes] = await Promise.all([
        axios.get(`${API}/markets/holdings`, { params: item.assetFilter ? { asset_class: item.assetFilter } : {} }),
        axios.get(`${API}/markets/transactions`, { params: { limit: 5, ...(item.assetFilter ? { asset_class: item.assetFilter } : {}) } })
      ]);
      setPreviewHoldings(holdingsRes.data?.slice(0, 5) || []);
      setPreviewTxns(txnsRes.data?.slice(0, 5) || []);
    } catch {
      setPreviewHoldings([]);
      setPreviewTxns([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); handleFiles([...e.dataTransfer.files]); };
  const handleFileInput = (e) => handleFiles([...e.target.files]);

  const handleFiles = (files) => {
    const valid = files.filter(f => ['pdf','xlsx','xls','csv','html','txt'].includes(f.name.split('.').pop().toLowerCase()));
    if (valid.length !== files.length) toast.error('Some files skipped — only PDF, Excel, CSV, HTML supported.');
    setUploadedFiles(prev => [...prev, ...valid.map(f => ({
      id: Date.now() + Math.random(), name: f.name,
      size: f.size, type: f.name.split('.').pop().toUpperCase(), status: 'pending', file: f
    }))]);
  };
  const removeFile = (id) => setUploadedFiles(prev => prev.filter(f => f.id !== id));

  const processImport = async () => {
    if (!uploadedFiles.length) { toast.error('Please upload at least one file'); return; }
    setImporting(true);
    for (let i = 0; i < uploadedFiles.length; i++) {
      setUploadedFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'processing' } : f));
      await new Promise(r => setTimeout(r, 1500));
      setUploadedFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'success', records: Math.floor(Math.random() * 20) + 1 } : f));
    }
    setImporting(false);
    toast.success('Import completed successfully!');
  };

  const formatFileSize = (b) => b < 1024 ? b + ' B' : b < 1048576 ? (b/1024).toFixed(1)+' KB' : (b/1048576).toFixed(1)+' MB';
  const formatCurrency = (v) => fmtCur(v, user?.market);

  return (
    <div className="p-6" data-testid="data-import-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
          <Upload className="w-5 h-5 text-[#1a1a1a]" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[#1a1a1a]">Import Data</h1>
          <p className="text-sm text-[#4a4a4a]">Auto-import from brokers, depositories & institutions</p>
        </div>
      </div>

      <Tabs defaultValue="import" className="w-full">
        <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-6">
          <TabsTrigger value="import" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Import</TabsTrigger>
          <TabsTrigger value="sharepoint" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">SharePoint / OneDrive</TabsTrigger>
          <TabsTrigger value="portfolios" className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Manage Portfolios</TabsTrigger>
        </TabsList>

        <TabsContent value="portfolios">
          <PortfolioManagementPage />
        </TabsContent>

        {/* ── SharePoint Tab ─────────────────────────────────────────── */}
        <TabsContent value="sharepoint">
          <div className="space-y-6">

            {/* Connect button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-[#1a1a1a]">SharePoint / OneDrive Connections</h2>
                <p className="text-sm text-[#4a4a4a] mt-0.5">Connect a SharePoint folder to automatically sync and extract documents into your data room.</p>
              </div>
              <Button
                onClick={connectSharePoint}
                disabled={spLoading}
                className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black gap-2"
              >
                {spLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Connect SharePoint
              </Button>
            </div>

            {/* Connection cards */}
            {spConnections.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#e5e5e5] bg-[#f9f9f9] py-16 flex flex-col items-center gap-3 text-[#4a4a4a]">
                <CloudOff className="w-10 h-10" />
                <p className="font-medium">No connections yet</p>
                <p className="text-sm">Click "Connect SharePoint" to link a folder</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {spConnections.map(conn => {
                  const prog = spProgress[conn.id];
                  const isSyncing = spSyncing === conn.id;
                  return (
                    <div key={conn.id} className="rounded-xl border border-[#e5e5e5] bg-white p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
                            <FolderOpen className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#1a1a1a] text-sm">
                              {conn.connected_folder_name || 'No folder set'}
                            </p>
                            {conn.folder_url ? (
                              <a href={conn.folder_url} target="_blank" rel="noreferrer"
                                className="text-xs text-[#1a1a1a] hover:underline flex items-center gap-1 mt-0.5">
                                <ExternalLink className="w-3 h-3" />
                                <span className="truncate max-w-[280px]">{conn.folder_url}</span>
                              </a>
                            ) : (
                              <p className="text-xs text-amber-600 mt-0.5">Set a folder URL to enable sync</p>
                            )}
                            {conn.last_synced_at && (
                              <p className="text-xs text-[#4a4a4a] mt-0.5">
                                Last synced: {new Date(conn.last_synced_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button size="sm" variant="outline"
                            onClick={() => openFolderDialog(conn.id)}
                            className="gap-1.5 text-xs h-8">
                            <Folder className="w-3.5 h-3.5" /> Set Folder
                          </Button>
                          <Button size="sm"
                            disabled={!conn.drive_id || isSyncing}
                            onClick={() => syncConnection(conn.id)}
                            className="gap-1.5 text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                            {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            {isSyncing ? 'Syncing…' : 'Sync'}
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => deleteConnection(conn.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Sync progress bar */}
                      {prog && !prog.done && prog.total > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between text-xs text-[#4a4a4a] mb-1">
                            <span>{prog.completed + prog.failed} / {prog.total} files</span>
                            {prog.eta > 0 && <span>~{prog.eta}s remaining</span>}
                          </div>
                          <div className="w-full h-2 bg-[#f5f5f5] rounded-full overflow-hidden">
                            <div
                              className="h-2 bg-[#ffd879] rounded-full transition-all duration-300"
                              style={{ width: `${prog.total > 0 ? Math.round(((prog.completed + prog.failed) / prog.total) * 100) : 0}%` }}
                            />
                          </div>
                          {prog.failed > 0 && (
                            <p className="text-xs text-amber-600 mt-1">{prog.failed} file(s) failed</p>
                          )}
                        </div>
                      )}
                      {prog?.done && (
                        <div className="mt-3 flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 text-xs font-medium">
                          <CheckCircle className="w-4 h-4" />
                          {prog.message || 'Sync complete'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Synced documents */}
            {spDocs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#1a1a1a] mb-3">Data Room Documents ({spDocs.length})</h3>
                <div className="rounded-xl border border-[#e5e5e5] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a1a1a]">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Filename</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Type</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Size</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Extracted</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Added</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {spDocs.map(doc => (
                        <tr key={doc.id} className="hover:bg-[#f9f9f9]">
                          <td className="px-4 py-2.5 font-medium text-[#1a1a1a]">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-[#4a4a4a] flex-shrink-0" />
                              <span className="truncate max-w-[220px]">{doc.filename}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-[#4a4a4a] text-xs">{doc.file_type?.split('/').pop()?.toUpperCase() || '—'}</td>
                          <td className="px-4 py-2.5 text-right text-[#4a4a4a] text-xs">
                            {doc.file_size ? (doc.file_size < 1048576 ? `${(doc.file_size/1024).toFixed(0)} KB` : `${(doc.file_size/1048576).toFixed(1)} MB`) : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {doc.extraction_status === 'done'
                              ? <span className="text-emerald-600 text-xs font-medium flex items-center justify-end gap-1"><CheckCircle className="w-3.5 h-3.5" /> Done</span>
                              : <span className="text-amber-600 text-xs">{doc.extraction_status}</span>
                            }
                          </td>
                          <td className="px-4 py-2.5 text-right text-[#4a4a4a] text-xs">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button onClick={() => deleteDocument(doc.id)}
                              className="text-[#4a4a4a] hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Set Folder Dialog */}
          <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Set SharePoint Folder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-[#4a4a4a]">
                  Paste a SharePoint folder URL or sharing link. The folder will be resolved via Microsoft Graph.
                </p>
                <input
                  type="url"
                  value={folderUrl}
                  onChange={e => setFolderUrl(e.target.value)}
                  placeholder="https://tenant.sharepoint.com/sites/SiteName/Shared Documents/FolderName"
                  className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
                  <Button onClick={saveFolder} className="bg-[#ffd879] hover:bg-[#f5cc6a] text-black">Save Folder</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="import">
          <div className="space-y-8">

            {/* ── Import Category Selector ── */}
            <div>
              <h2 className="text-base font-semibold text-[#1a1a1a] mb-3">What would you like to import?</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {IMPORT_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const active = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(active ? '' : cat.id)}
                      className={`rounded-2xl overflow-hidden border text-left transition-all hover:shadow-md ${active ? 'border-[#ffd87a] ring-2 ring-[#ffd87a]' : 'border-[#e5e5e5] hover:border-[#e5e5e5]'}`}
                    >
                      <div className={`${cat.color} px-4 py-3 flex items-center gap-3`}>
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-semibold text-white leading-tight">{cat.label}</span>
                        {active && <Check className="w-4 h-4 text-white ml-auto shrink-0" />}
                      </div>
                      <div className="bg-white px-3 py-2">
                        <p className="text-xs text-[#4a4a4a] leading-relaxed">{cat.sources[0]}{cat.sources.length > 1 ? ` + ${cat.sources.length - 1} more` : ''}</p>
                        <p className="text-xs text-[#4a4a4a] mt-0.5">{cat.formats}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedCategory && (
                <div className="mt-3 p-3 bg-[#f9f9f9] border border-[#e5e5e5] rounded-xl text-sm text-[#1a1a1a] flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>{IMPORT_CATEGORIES.find(c => c.id === selectedCategory)?.label}</strong> — Accepted sources:{' '}
                    {IMPORT_CATEGORIES.find(c => c.id === selectedCategory)?.sources.join(', ')}
                  </span>
                </div>
              )}
            </div>

            {/* ── Import Sources (from DB config) ── */}
            {importSources.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-[#1a1a1a] mb-3">Select Import Source</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {importSources.map(source => (
                    <button
                      key={source.id}
                      onClick={() => setSelectedSource(source.id)}
                      className={`p-4 rounded-xl border transition-all text-left ${selectedSource === source.id ? 'bg-[#f9f9f9] border-[#ffd87a]' : 'bg-white border-[#e5e5e5] hover:border-[#e5e5e5]'}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-11 h-11 rounded-xl ${source.color} flex items-center justify-center shrink-0`}>
                          <source.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1a1a1a] mb-1">{source.name}</p>
                          <p className="text-sm text-[#4a4a4a] mb-2">{source.description}</p>
                          <div className="flex items-center gap-2 text-xs text-[#4a4a4a]">
                            <span>{source.formats}</span>
                            {source.brokers && <><span>•</span><span>{source.brokers} brokers</span></>}
                          </div>
                        </div>
                        {selectedSource === source.id && <Check className="w-5 h-5 text-[#ffd87a] shrink-0" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── File Upload ── */}
            <div>
              <h2 className="text-base font-semibold text-[#1a1a1a] mb-3">Upload Files</h2>
              <div
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${dragActive ? 'border-[#ffd87a] bg-[#f9f9f9]' : 'border-[#e5e5e5] hover:border-[#a8a8a8] bg-white'}`}
              >
                <input type="file" multiple accept=".pdf,.xlsx,.xls,.csv,.html,.txt" onChange={handleFileInput} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                <Upload className={`w-10 h-10 mx-auto mb-3 ${dragActive ? 'text-[#ffd87a]' : 'text-[#4a4a4a]'}`} />
                <p className="text-base font-medium text-[#1a1a1a] mb-1">{dragActive ? 'Drop files here' : 'Drag & drop files here'}</p>
                <p className="text-sm text-[#4a4a4a] mb-3">or click to browse your device</p>
                <p className="text-xs text-[#4a4a4a]">PDF · Excel (.xlsx / .xls) · CSV · HTML · TXT</p>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {uploadedFiles.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-white border border-[#e5e5e5] rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#f9f9f9] flex items-center justify-center">
                          <FileText className="w-4 h-4 text-[#1a1a1a]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1a1a1a]">{file.name}</p>
                          <p className="text-xs text-[#4a4a4a]">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {file.status === 'pending' && <span className="text-xs text-[#4a4a4a]">Ready</span>}
                        {file.status === 'processing' && <div className="flex items-center gap-1.5 text-[#ffd87a] text-xs"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Processing…</div>}
                        {file.status === 'success' && <div className="flex items-center gap-1.5 text-emerald-600 text-xs"><CheckCircle className="w-4 h-4" /> {file.records} records</div>}
                        {file.status === 'pending' && <button onClick={() => removeFile(file.id)} className="p-1 rounded hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-red-400"><X className="w-4 h-4" /></button>}
                      </div>
                    </div>
                  ))}
                  {uploadedFiles.some(f => f.status === 'pending') && (
                    <Button onClick={processImport} disabled={importing} className="mt-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white">
                      {importing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Importing…</> : <><Upload className="w-4 h-4 mr-2" />Import {uploadedFiles.filter(f => f.status === 'pending').length} Files</>}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* ── Supported Formats ── */}
            <div>
              <h2 className="text-base font-semibold text-[#1a1a1a] mb-3">Supported Sources & Formats</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {SUPPORTED_FORMATS.map(fmt => (
                  <div key={fmt.label} className="flex items-start gap-3 p-3 bg-white border border-[#e5e5e5] rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-[#1a1a1a] flex items-center justify-center shrink-0 mt-0.5">
                      <Database className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1a1a1a]">{fmt.label}</p>
                      <p className="text-xs text-[#4a4a4a] mt-0.5">{fmt.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Broker pills */}
              {popularBrokers.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wide mb-2">Supported Brokers</p>
                  <div className="flex flex-wrap gap-2">
                    {popularBrokers.map(b => (
                      <span key={b} className="px-3 py-1.5 rounded-lg bg-white border border-[#e5e5e5] text-sm text-[#4a4a4a]">{b}</span>
                    ))}
                    <span className="px-3 py-1.5 rounded-lg bg-[#f5f5f5] text-sm text-[#4a4a4a]">+685 more</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Recent Imports ── */}
            <div>
              <h2 className="text-base font-semibold text-[#1a1a1a] mb-3">Recent Imports</h2>
              <div className="rounded-2xl overflow-hidden border border-[#e5e5e5] shadow-sm">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#1a1a1a]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Type</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Records</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e5e5]">
                    {recentImports.map((item, idx) => (
                      <tr key={item.id} className={`transition-colors hover:bg-[#f9f9f9] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}`}>
                        <td className="px-4 py-3 text-sm text-[#4a4a4a]">{item.date}</td>
                        <td className="px-4 py-3 text-sm font-medium text-[#1a1a1a]">{item.source}</td>
                        <td className="px-4 py-3 text-sm text-[#4a4a4a]">{item.type}</td>
                        <td className="px-4 py-3 text-right text-sm text-[#2a2a2a] font-medium">{item.records}</td>
                        <td className="px-4 py-3 text-center">
                          {item.status === 'success'
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3" />Success</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3" />Partial</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => openPreview(item)} className="p-1.5 rounded-lg hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#1a1a1a] transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── API Refresh Info ── */}
            <div className="p-4 bg-white border border-[#e5e5e5] rounded-xl flex items-start gap-3">
              <Info className="w-5 h-5 text-[#ffd87a] shrink-0 mt-0.5" />
              <div className="text-sm text-[#4a4a4a]">
                <p className="font-medium mb-1">MF CAS API Refresh</p>
                <p className="text-[#4a4a4a]">Auto-fetch your Mutual Fund CAS directly from CAMS / NSDL. You have 20 free refreshes remaining this month.</p>
                <Button variant="outline" size="sm" className="mt-3 border-[#e5e5e5] text-[#4a4a4a]">
                  <RefreshCw className="w-4 h-4 mr-2" /> Fetch MF CAS via API
                </Button>
              </div>
            </div>

          </div>
        </TabsContent>
      </Tabs>

      {/* ── Preview Modal ── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a] text-base">{previewTitle}</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-[#ffd87a] animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {previewHoldings.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wide mb-2">Holdings Imported</p>
                  <div className="rounded-xl overflow-hidden border border-[#e5e5e5]">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1a1a1a]">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Symbol</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Qty</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Value</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">P&L</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {previewHoldings.map((h, i) => (
                          <tr key={i} className="hover:bg-[#f9f9f9]">
                            <td className="px-4 py-2.5 font-medium text-[#1a1a1a]">{h.symbol}<span className="ml-2 text-xs text-[#4a4a4a]">{h.name?.substring(0, 18)}</span></td>
                            <td className="px-4 py-2.5 text-right text-[#4a4a4a]">{h.quantity}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-[#1a1a1a]">{formatCurrency(h.current_value)}</td>
                            <td className={`px-4 py-2.5 text-right font-semibold ${(h.gain_percent||0) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                              {(h.gain_percent||0) >= 0 ? '+' : ''}{(h.gain_percent||0).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {previewTxns.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-wide mb-2">Recent Transactions</p>
                  <div className="rounded-xl overflow-hidden border border-[#e5e5e5]">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1a1a1a]">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Symbol</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Type</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Qty</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Amount</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#ffd87a]">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {previewTxns.map((t, i) => (
                          <tr key={i} className="hover:bg-[#f9f9f9]">
                            <td className="px-4 py-2.5 font-medium text-[#1a1a1a]">{t.symbol}</td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.transaction_type === 'SELL' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {t.transaction_type}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right text-[#4a4a4a]">{t.quantity}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-[#1a1a1a]">{formatCurrency(t.amount)}</td>
                            <td className="px-4 py-2.5 text-right text-[#4a4a4a]">{t.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!previewHoldings.length && !previewTxns.length && (
                <p className="text-center text-[#4a4a4a] py-8">No data found for this import.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

